import {Tile, TileId, TileIdMap, TILE_TYPE_SUPER_FOLDER, TILE_TYPE_SUPER_CANVAS} from '../../shared/models/tile';
import {SuperFolderStructure, CanvasStructure} from '../../shared/models/structure';
import {Filters} from '../../shared/models/filter';
import {SearchService} from './search.service';
import {AppTileTree} from '../../shared/models/app-tile-tree';

export class AppTileFilterHelper {

  static buildTileTree(topLevelTiles: TileIdMap, sessUserTiles: TileIdMap): AppTileTree {
    const flatTiles: Tile[] = [];
    const childrenOf = new Map<TileId, TileId[]>();

    for (const tile of Object.values(topLevelTiles)) {
      flatTiles.push(tile);

      if (!tile.typeSupportsHavingDescendants()) {
        continue;
      }

      let childIds: TileId[] = [];
      if (tile.Type === TILE_TYPE_SUPER_FOLDER) {
        childIds = (tile.Structure as SuperFolderStructure)?.TileIDs ?? [];
      } else if (tile.Type === TILE_TYPE_SUPER_CANVAS) {
        childIds = (tile.Structure as CanvasStructure).Points?.flatMap(p => p.TileIDs) ?? [];
      }

      childrenOf.set(tile.ID, childIds);

      for (const childId of childIds) {
        const child = sessUserTiles[childId];
        if (child) {
          flatTiles.push(child);
        }
      }
    }

    return {flatTiles, childrenOf};
  }

  static computeFilteredTileIds(
    topLevelTiles: TileIdMap,
    tree: AppTileTree,
    searchTerm: string,
    filters: Filters | undefined,
    searchService: SearchService,
  ): Set<TileId> {
    let matchingTiles: Tile[] = tree.flatTiles;

    if (searchTerm) {
      matchingTiles = matchingTiles.filter(t => searchService.tileMatchesSelf(searchTerm, t));
    }

    if (filters) {
      matchingTiles = searchService.filterTiles(filters, matchingTiles);
    }

    const selfMatchIds = new Set(matchingTiles.map(t => t.ID));
    const visibleIds = new Set<TileId>();

    for (const tile of Object.values(topLevelTiles)) {
      const children = tree.childrenOf.get(tile.ID);

      if (!children) {
        if (selfMatchIds.has(tile.ID)) {
          visibleIds.add(tile.ID);
        }
        continue;
      }

      if (selfMatchIds.has(tile.ID)) {
        visibleIds.add(tile.ID);
        children.forEach(id => visibleIds.add(id));
      } else {
        const matchingChildren = children.filter(id => selfMatchIds.has(id));
        if (matchingChildren.length > 0) {
          visibleIds.add(tile.ID);
          matchingChildren.forEach(id => visibleIds.add(id));
        }
      }
    }

    return visibleIds;
  }
}
